import React from "react";

// External
import styled, { css } from "styled-components";
import { FiChevronLeft } from "react-icons/fi";
import { navigate } from "gatsby";

// Internal
import useMediaQuery from "../../hooks/use-media-query";
import IconButton from "../../components/form/IconButton";
import { Flex } from "../../styles/global-styles";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";
import { fontFamily, colors, breakpoints, sectionRhythm } from "../../../theme";
import { PROJECT_CONDEXO_PAY } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import Testimonials from "../reviews-section/Testimonials";

// Styles

const BlogContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }

  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const BlogTitle = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const Video = styled.video`
  height: 102%;
  border-radius: 6px !important;
  transform: scale(1.02);

  @media only screen and (min-width: ${breakpoints.tablet}) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  width: ${(props) => (props.isMobileCover ? "96%" : "100%")};
  border: 4px solid white;
  border-radius: 6px !important;
  overflow: hidden;
  box-sizing: border-box;
  background-image: linear-gradient(180deg, ${colors.white}, ${colors.white});
  box-shadow: 17px 17px 48px 0 rgb(0 0 0 / 12%);
  margin-top: 1.5rem;
`;

const BlogContentContainer = styled.div`
  ${caseStudyBodyStyles}
`;

const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  margin-bottom: 0;
`;

const OverviewItemContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: 1000px) {
    flex-direction: row;
  }
`;

const OverviewItem = styled.div`
  margin-bottom: 1rem;

  ${(props) =>
    props.isSeventyPercent &&
    css`
      width: 100%;

      @media only screen and (min-width: 1000px) {
        width: 70%;
      }
    `}

  ${(props) =>
    props.isThirtyPercent &&
    css`
      width: 100%;

      @media only screen and (min-width: 1000px) {
        width: 30%;
      }
    `}
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;

  a,
  &[href] {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

/* Architecture */

const boxHelperTypography = css`
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: ${colors.grey.darkMedium};
`;

const diagramHelperTypography = boxHelperTypography;

const diagramHelperText = css`
  ${boxHelperTypography}
  display: block;
`;

const ArchitectureDiagram = styled.div`
  margin: 2.5rem 0 3rem;
  padding: 1.25rem 0;
  box-sizing: border-box;
`;

const ArchitectureNode = styled.div`
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
  padding: 0.9rem 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  text-align: center;
  background: ${colors.white};
  box-sizing: border-box;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 3px;
  }
`;

const ArchitectureArrow = styled.div`
  width: 1px;
  height: 28px;
  margin: 0 auto;
  background: black;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 7px;
    height: 7px;
    border-right: 1px solid black;
    border-bottom: 1px solid black;
    transform: translateX(-50%) rotate(45deg);
    background: transparent;
  }
`;

const ArchitectureSplit = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
  }
`;

const ArchitectureBranch = styled.div`
  position: relative;
  padding-top: 0;

  &:first-child {
    padding-top: 1.5rem;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    padding-top: 1.5rem;
  }
`;

const ArchitectureLabel = styled.div`
  margin-bottom: 0.8rem;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
  color: ${colors.grey.darkMedium};
`;

const ServiceFlow = styled.div`
  display: grid;
  gap: 0;
`;

const ServiceFlowArrow = styled(ArchitectureArrow)`
  height: 24px;
`;

const ClientStateNode = styled(ArchitectureNode)`
  margin-top: 0;
`;

const ServiceStateNode = styled(ArchitectureNode)`
  margin-top: 0;
`;

const DecisionSection = styled.div`
  margin-top: 0;
`;

const DecisionBlock = styled.div`
  margin-top: 0;
  padding-top: 2rem;

  &:first-child {
    padding-top: 0;
  }

  & > h5 {
    margin-top: 0;
  }
`;

const DecisionTitle = styled.h5`
  margin: 0 0 0.75rem;
  font-family: ${fontFamily.extraBold};
  font-size: 16px;
  line-height: 1.3;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 18px;
  }
`;

const DecisionQuestion = styled.p`
  margin: 1.25rem 0 1.5rem;
  font-weight: 600;
`;

const DecisionFlow = styled.div`
  margin: 2rem auto;
  display: grid;
  gap: 1rem;
  max-width: 420px;
`;

const DecisionFlowNode = styled.div`
  padding: 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  background: ${colors.white};
  text-align: center;

  strong {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 14px;
  }

  small {
    ${diagramHelperText}
  }
`;

const DecisionComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 2rem 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DecisionCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 10px;
  background: ${colors.white};

  h6 {
    margin: 0 0 0.75rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  ul,
  ol {
    margin: 0;
    padding-left: 1.25rem;
    list-style: none;
  }

  li {
    ${boxHelperTypography}
    display: list-item;
    margin: 0 0 0.45rem 1rem;
    padding: 0;
    letter-spacing: -0.003em;
    list-style-position: outside;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 14px;
      line-height: 1.45;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li {
    list-style-type: disc;
  }

  ol > li {
    list-style-type: decimal;
  }
`;

const ArchitectureEvolution = styled.div`
  margin: 2.5rem 0;
  display: grid;
  gap: 1rem;
  align-items: center;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr 40px 1fr;
  }
`;

const ArchitectureEvolutionColumn = styled.div`
  min-width: 0;
`;

const ArchitectureEvolutionLabel = styled.div`
  margin-bottom: 0.75rem;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.grey.darkMedium};
`;

const ArchitectureEvolutionArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 24px;
  margin: 0 auto;
  transform: rotate(90deg);

  &::before {
    content: "";
    display: block;
    width: 28px;
    height: 2px;
    background: black;
  }

  &::after {
    content: "";
    display: block;
    width: 7px;
    height: 7px;
    margin-left: -4px;
    border-right: 2px solid black;
    border-bottom: 2px solid black;
    transform: rotate(-45deg);
    background: transparent;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    transform: none;
  }
`;

const ArchitectureStack = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const ArchitectureStackItem = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  background: ${colors.white};

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 0.2rem;
  }
`;

const FeatureFileDiagram = styled(ArchitectureEvolution)`
  margin: 2rem 0;
`;

const FeatureFileList = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const FeatureFileItem = styled.div`
  padding: 0.65rem 0.85rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  background: ${colors.white};

  strong {
    display: block;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    font-family: ${fontFamily.medium};
  }

  small {
    ${diagramHelperText}
    margin-top: 0.15rem;
  }
`;

const FeatureFileNote = styled.div`
  margin-top: 0.65rem;
  ${boxHelperTypography}
  text-align: center;
`;

const GalleryContentContainer = styled.div`
  margin-block: 2rem;
`;

const GalleryContentItem = styled.div`
  margin-block: 2rem;
  position: relative;
`;

const VideoCaption = styled.span`
  font-size: 0.8rem;
  text-align: center;
  width: 100%;
  display: inline-block;
  margin-top: 0.8rem;
`;

// Diagrams

const ExistingArchitectureDiagram = () => (
  <ArchitectureDiagram>
    <ArchitectureNode>
      <strong>React Application</strong>
      <small>UI and feature layer</small>
    </ArchitectureNode>

    <ArchitectureArrow />

    <ArchitectureNode>
      <strong>Redux</strong>
      <small>Central state layer</small>
    </ArchitectureNode>

    <ArchitectureArrow />

    <ArchitectureSplit>
      <ArchitectureBranch>
        <ArchitectureLabel>Client state</ArchitectureLabel>
        <ClientStateNode>
          <strong>Frontend-owned state</strong>
          <small>State created and controlled by the application</small>
        </ClientStateNode>
      </ArchitectureBranch>

      <ArchitectureBranch>
        <ArchitectureLabel>Server state</ArchitectureLabel>
        <ServiceFlow>
          <ServiceStateNode>
            <strong>Backend-derived data</strong>
            <small>Data originating from service APIs</small>
          </ServiceStateNode>

          <ServiceFlowArrow />

          <ArchitectureNode>
            <strong>Redux Saga</strong>
            <small>Async service orchestration</small>
          </ArchitectureNode>

          <ServiceFlowArrow />

          <ArchitectureNode>
            <strong>Backend APIs</strong>
            <small>Remote data source</small>
          </ArchitectureNode>
        </ServiceFlow>
      </ArchitectureBranch>
    </ArchitectureSplit>
  </ArchitectureDiagram>
);

const StateOwnershipDecisionFlow = () => (
  <DecisionFlow>
    <DecisionFlowNode>
      <strong>Client state</strong>
      <small>Owned by the frontend and driven by UI behaviour</small>
    </DecisionFlowNode>

    <DecisionFlowNode>
      <strong>Server state</strong>
      <small>Originates from and is synchronized with backend APIs</small>
    </DecisionFlowNode>
  </DecisionFlow>
);

const FinalArchitectureDiagram = () => (
  <ArchitectureDiagram>
    <ArchitectureNode>
      <strong>React Application</strong>
      <small>UI and feature layer</small>
    </ArchitectureNode>

    <ArchitectureArrow />

    <ArchitectureSplit>
      <ArchitectureBranch>
        <ArchitectureLabel>Client state</ArchitectureLabel>
        <ClientStateNode>
          <strong>Zustand</strong>
          <small>Frontend-owned application state</small>
        </ClientStateNode>
      </ArchitectureBranch>

      <ArchitectureBranch>
        <ArchitectureLabel>Server state</ArchitectureLabel>
        <ServiceFlow>
          <ServiceStateNode>
            <strong>React Query</strong>
            <small>Server-state lifecycle and caching</small>
          </ServiceStateNode>

          <ServiceFlowArrow />

          <ArchitectureNode>
            <strong>Backend APIs</strong>
            <small>Remote source of truth</small>
          </ArchitectureNode>
        </ServiceFlow>
      </ArchitectureBranch>
    </ArchitectureSplit>
  </ArchitectureDiagram>
);

const ApiFeatureFileDiagram = () => (
  <FeatureFileDiagram>
    <ArchitectureEvolutionColumn>
      <ArchitectureEvolutionLabel>
        Per feature: Redux + Saga
      </ArchitectureEvolutionLabel>

      <FeatureFileList>
        <FeatureFileItem>
          <strong>payments.actions.js</strong>
          <small>Request / success / failure actions</small>
        </FeatureFileItem>
        <FeatureFileItem>
          <strong>payments.reducer.js</strong>
          <small>Loading, error, and response state</small>
        </FeatureFileItem>
        <FeatureFileItem>
          <strong>payments.saga.js</strong>
          <small>Async API orchestration</small>
        </FeatureFileItem>
        <FeatureFileItem>
          <strong>payments.selectors.js</strong>
          <small>Select data from the store</small>
        </FeatureFileItem>
      </FeatureFileList>

      <FeatureFileNote>Repeated for every API-backed feature</FeatureFileNote>
    </ArchitectureEvolutionColumn>

    <ArchitectureEvolutionArrow />

    <ArchitectureEvolutionColumn>
      <ArchitectureEvolutionLabel>
        Per feature: React Query
      </ArchitectureEvolutionLabel>

      <FeatureFileList>
        <FeatureFileItem>
          <strong>usePayments.js</strong>
          <small>Query hook with built-in lifecycle</small>
        </FeatureFileItem>
        <FeatureFileItem>
          <strong>payments.api.js</strong>
          <small>API call only</small>
        </FeatureFileItem>
      </FeatureFileList>

      <FeatureFileNote>
        Loading, error, and cache handled by React Query
      </FeatureFileNote>
    </ArchitectureEvolutionColumn>
  </FeatureFileDiagram>
);

const CondexoPayProjectSection = () => {
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.tablet} )`);

  const goBack = () => {
    navigate(-1);
  };

  const randomData = {
    date: new Date(),
    title: "Simplifying & securing online payment through all-in-one app",
    clientName: "Condexo",
    projectName: "Condexo Pay",
    role: "Frontend developer",
    year: "2019-2020",
    cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038674/projects/condexo-pay/condexo-pay__landing-page--old_zxl90m_gq9t2z.mp4",
    mobile_cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038638/projects/condexo-pay/condex-pay__landing-page--mobile_dfw2r8_mpvl3n.mp4",
    clientCountry: "Italy",
    servicesProvided: "Front-end development",
    industry: "FinTech",
    techStack: [
      "Javascript",
      "React",
      "Zustand",
      "React Query",
      "Material UI",
      "Stripe",
    ],
    projectLink: "https://pay.condexo.it/",
    clientReviews: [
      {
        name: "Elisa Fiorenzani",
        position: "UX/UI designer",
        company: "Condexo",
        profilePic: "elisa-condexo.jpeg",
        text: "It was a pleasure to work with Parmeet, an honest and reliable Dev!",
      },
    ],
  };

  const mediaLinks = {
    myPayments:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038599/projects/condexo-pay/condexo-pay__my-payments_crskpm_xlqpif.mp4",
    bolletini:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038634/projects/condexo-pay/condexo-pay__bolettini-flow_rmytnt_vn8uf3.mp4",
    giftCard:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038626/projects/condexo-pay/condexo-pay__gift-card_nmzwxf_fqtwhi.mp4",
  };

  return (
    <>
      <BlogContainer>
        {isMobile && (
          <Flex spaceBetween>
            <IconButton
              bgColor="#bbb"
              iconColor="#000"
              style={{ marginLeft: "-12px" }}
              onClick={goBack}
              ariaLabel="Go back"
            >
              <FiChevronLeft />
            </IconButton>
          </Flex>
        )}

        <ContainerScroll
          titleComponent={<BlogTitle>{randomData.title}</BlogTitle>}
          role={randomData.role}
        >
          <ProjectHero
            src={PROJECT_CONDEXO_PAY.media.hero}
            alt={randomData.title}
          />
        </ContainerScroll>

        <OverviewSection>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{randomData.clientName}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{randomData.clientCountry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>

          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{randomData.servicesProvided}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{randomData.industry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>

          {isMobile && (
            <OverviewItem isHundredPercent>
              <OverviewLabel>Year</OverviewLabel>
              <OverviewValue>{randomData.year}</OverviewValue>
            </OverviewItem>
          )}

          <OverviewItem isHundredPercent>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{randomData.techStack.join(", ")}</OverviewValue>
          </OverviewItem>

          {randomData.projectLink ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={randomData.projectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {randomData.projectLink}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <BlogContentContainer>
          <section>
            <h4>About the product</h4>
            <p>
              Condexo was building Condexo Pay, a fintech platform intended to
              bring everyday payments and services into a single digital
              experience. The MVP brought together workflows such as utility
              payments, mobile recharges, gift cards, and payment-related
              services that were traditionally handled through separate
              channels.
            </p>

            <p>
              I joined the project as the frontend developer, working with the
              existing product architecture and turning the MVP requirements and
              designs into a reliable, responsive frontend.
            </p>

            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.myPayments} />
            </VideoContainer>
          </section>

          <section>
            <h4>The System I was added to</h4>

            <p>
              When I joined, the product was already being developed with its
              core architecture in place. The team consisted of a project
              manager, a backend developer, and me responsible for the frontend,
              working alongside the client's stakeholders and in-house designer.
            </p>

            <p>
              The frontend was built with React, with Redux acting as the
              central state layer and Redux Saga coordinating asynchronous
              communication with backend services. In practice, client state and
              data originating from APIs were both being represented through the
              same state-management layer.
            </p>

            <ExistingArchitectureDiagram />

            <p>
              This architecture provided a solid foundation for the initial
              product. As more payment services and application workflows were
              introduced, however, the responsibilities of the state layer
              became increasingly intertwined. A feature could require
              additional actions, reducers, selectors, and saga logic even when
              the underlying data ultimately originated from a backend service.
            </p>

            <p>
              That led to a more fundamental question: should client state and
              server state be managed in the same way?
            </p>

            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.bolletini} />
            </VideoContainer>
          </section>

          <section>
            <h4>Decisions</h4>

            <DecisionSection>
              <DecisionBlock>
                <DecisionTitle>
                  1. Separate client state from server state
                </DecisionTitle>

                <p>
                  The first decision was not about replacing Redux with another
                  library. It was about understanding what the application was
                  actually asking its state-management layer to do.
                </p>

                <DecisionQuestion>
                  "Should state owned by the frontend and state owned by the
                  backend share the same management model?"
                </DecisionQuestion>

                <p>
                  Client state represents decisions and interactions that belong
                  to the frontend: UI state, local selections, and other values
                  controlled by the application. Server state has a different
                  lifecycle. It originates from backend APIs, can be consumed by
                  multiple parts of the application, and needs to account for
                  request, loading, error, caching, and refresh behaviour.
                </p>

                <StateOwnershipDecisionFlow />

                <p>
                  Treating both categories as one problem meant that API
                  interactions had to be represented through the same Redux
                  machinery used for client state. I proposed separating the two
                  responsibilities so that each could be handled according to
                  its actual lifecycle.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>2. Client state: Zustand</DecisionTitle>

                <p>
                  Once client state was its own concern, the next question was
                  how to manage it without recreating Redux ceremony for values
                  the frontend already owned. The criteria were simple: low
                  boilerplate, incremental adoption in an existing codebase, and
                  a clear boundary from API-derived data.
                </p>

                <p>
                  Redux Toolkit was the safest path because the project already
                  used Redux, but it would still keep client state inside the
                  same store model we were trying to shrink. React Context with
                  useReducer avoided a new dependency, but it did not scale
                  cleanly as more features needed shared UI state. Zustand fit
                  the need better: a small store API that components could use
                  directly, without actions, reducers, or selectors for local
                  application state.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Existing approach: Redux</h6>
                    <ul>
                      <li>Centralized application state</li>
                      <li>Actions and reducers around feature state</li>
                      <li>Additional selectors where required</li>
                      <li>Saga layer for asynchronous behaviour</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Proposed approach: Zustand</h6>
                    <ul>
                      <li>Lightweight store for client-owned state</li>
                      <li>Less feature-level boilerplate</li>
                      <li>Direct access to the state required by components</li>
                      <li>Clearer boundary between local state and API data</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  The important change was not simply reducing the number of
                  files. It was reducing the abstraction required to represent
                  state that was already local to the application. The trade-off
                  was less formal structure than Redux, which was acceptable for
                  the size of the MVP and the kinds of client state we needed to
                  hold.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>3. Server state: React Query</DecisionTitle>

                <p>
                  For API-derived data, the requirement was different: request
                  lifecycle, loading and error state, caching, and refresh
                  behaviour had to be handled without rebuilding that machinery
                  for every feature. Staying on Redux Saga was possible, but it
                  would have preserved the same multi-file pattern we were
                  trying to escape.
                </p>

                <p>
                  SWR offered a similar mental model with a lighter surface
                  area. RTK Query would have kept fetching inside the Redux
                  ecosystem, which worked against the separation we had just
                  defined. React Query was chosen because it treated server
                  state as its own concern: built-in request lifecycle, cache
                  ownership, and hooks that components could consume without
                  copying backend responses into a general-purpose store.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Redux + Saga</h6>
                    <ul>
                      <li>Explicit actions for requests and responses</li>
                      <li>Reducer state for loading and errors</li>
                      <li>Saga logic for asynchronous orchestration</li>
                      <li>Manual handling of service data lifecycle</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>React Query</h6>
                    <ul>
                      <li>Built-in request lifecycle</li>
                      <li>Loading and error states available to consumers</li>
                      <li>Caching of server-derived data</li>
                      <li>Data fetching separated from application state</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  In practice, that difference showed up in the file structure.
                  Each API-backed feature needed its own actions, reducer, saga,
                  and selectors just to move service data through Redux. React
                  Query collapsed that into a query hook and an API call.
                </p>

                <ApiFeatureFileDiagram />

                <p>
                  This changed the responsibility of the frontend state layer.
                  Instead of copying backend responses into a general-purpose
                  application store and maintaining their lifecycle manually,
                  server data could remain associated with the service that
                  owned it while React Query handled fetching, caching, and the
                  request lifecycle around that data.
                </p>

                <p>
                  The resulting separation gave each state type a clearer
                  responsibility: Zustand managed state the frontend owned,
                  while React Query managed data whose source of truth lived
                  behind the backend APIs. The trade-off was a new dependency
                  and a learning curve for the team, in exchange for removing
                  repeated API plumbing from every feature.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>4. Payments: Stripe</DecisionTitle>

                <p>
                  Payment processing was a product decision as much as a
                  frontend one. Condexo Pay needed secure card handling for an
                  Italian fintech MVP without taking PCI scope into the
                  application UI. Building directly against bank rails or a
                  lower-level processor would have slowed delivery and pushed
                  sensitive payment details into our frontend surface.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>What we needed</h6>
                    <ul>
                      <li>
                        Card payments without storing card data in the app
                      </li>
                      <li>A mature JS SDK that fit a React frontend</li>
                      <li>Clear payment status and webhook flows</li>
                      <li>A path that could ship with the MVP timeline</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Why Stripe</h6>
                    <ul>
                      <li>Elements kept card data in Stripe's PCI scope</li>
                      <li>
                        Documented APIs and dashboard for payment operations
                      </li>
                      <li>Webhooks and status model for payment history UI</li>
                      <li>Faster integration than custom processor work</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  Stripe became the payment layer for card and online payment
                  flows in the product. Domain-specific Italian services such as
                  bollettini still depended on their own backends and partners;
                  Stripe was not treated as a single solution for every payment
                  type, only for the card-processing surface the frontend had to
                  integrate safely and quickly.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>
                  5. The resulting frontend architecture
                </DecisionTitle>

                <p>
                  The architectural change was therefore less about introducing
                  two new libraries and more about establishing a clear
                  ownership boundary between two different kinds of state.
                </p>

                <ArchitectureEvolution>
                  <ArchitectureEvolutionColumn>
                    <ArchitectureEvolutionLabel>
                      Existing model
                    </ArchitectureEvolutionLabel>

                    <ArchitectureStack>
                      <ArchitectureStackItem>
                        <strong>React</strong>
                        <small>UI and feature layer</small>
                      </ArchitectureStackItem>

                      <ArchitectureStackItem>
                        <strong>Redux</strong>
                        <small>Application + service state</small>
                      </ArchitectureStackItem>

                      <ArchitectureStackItem>
                        <strong>Redux Saga</strong>
                        <small>API orchestration</small>
                      </ArchitectureStackItem>

                      <ArchitectureStackItem>
                        <strong>Backend APIs</strong>
                        <small>Remote data source</small>
                      </ArchitectureStackItem>
                    </ArchitectureStack>
                  </ArchitectureEvolutionColumn>

                  <ArchitectureEvolutionArrow />

                  <ArchitectureEvolutionColumn>
                    <ArchitectureEvolutionLabel>
                      Resulting model
                    </ArchitectureEvolutionLabel>

                    <ArchitectureStack>
                      <ArchitectureStackItem>
                        <strong>React</strong>
                        <small>UI and feature layer</small>
                      </ArchitectureStackItem>

                      <ArchitectureStackItem>
                        <strong>Zustand</strong>
                        <small>Frontend-owned application state</small>
                      </ArchitectureStackItem>

                      <ArchitectureStackItem>
                        <strong>React Query</strong>
                        <small>Server-state lifecycle and caching</small>
                      </ArchitectureStackItem>

                      <ArchitectureStackItem>
                        <strong>Backend APIs</strong>
                        <small>Remote source of truth</small>
                      </ArchitectureStackItem>
                    </ArchitectureStack>
                  </ArchitectureEvolutionColumn>
                </ArchitectureEvolution>

                <FinalArchitectureDiagram />

                <p>
                  With that foundation in place, the frontend could continue
                  expanding across the product's payment and service workflows
                  without forcing every feature to reproduce the same API
                  state-management plumbing.
                </p>
              </DecisionBlock>
            </DecisionSection>
          </section>

          <section>
            <h4>Outcome</h4>

            <p>
              The frontend was delivered as part of Condexo Pay's MVP,
              supporting a range of payment and service experiences within a
              single responsive application. The product could bring workflows
              such as utility payments, mobile recharge, gift cards, and payment
              history into one customer-facing platform.
            </p>

            <p>
              Beyond shipping the MVP, the frontend architecture evolved with
              the product. Separating client state from server state reduced the
              responsibility placed on a single state-management layer and gave
              subsequent features a clearer path for handling local interactions
              and backend data independently.
            </p>

            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials
              showMore={false}
              reviews={PROJECT_CONDEXO_PAY.client.reviews}
            />
          </section>
        </BlogContentContainer>
      </BlogContainer>

      <ContactMeSection />
    </>
  );
};

export default CondexoPayProjectSection;
